import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import { QueryClient } from "@tanstack/react-query";
import type { ControlPlaneClient } from "../controlplane/client";
import type { PlatformAdminApi } from "../controlplane/admin";
import type { AgentClient } from "../agent/client";

export interface ApiContextValue {
  readonly client: ControlPlaneClient;
  readonly admin: PlatformAdminApi;
  /**
   * The CDX Agent client. Optional because the assistant is a limited-preview
   * surface a deployment may not have: when it is absent the hooks report the
   * assistant as unavailable rather than the app failing to mount.
   */
  readonly agent?: AgentClient;
}

const ApiCtx = createContext<ApiContextValue | undefined>(undefined);

export function ApiProvider({
  value,
  children,
}: {
  readonly value: ApiContextValue;
  readonly children: ReactNode;
}) {
  return <ApiCtx.Provider value={value}>{children}</ApiCtx.Provider>;
}

export function useApi(): ApiContextValue {
  const ctx = useContext(ApiCtx);
  if (!ctx) throw new Error("useApi must be used within <ApiProvider>");
  return ctx;
}

/**
 * Failures a retry cannot fix, and would make worse. Re-sending a request that
 * was rejected for who you are, what you asked for, or how much you have
 * already spent only burns quota and delays the message the user needs.
 */
const NEVER_RETRY: ReadonlySet<string> = new Set([
  "unauthorized",
  "forbidden",
  "not_found",
  "validation_error",
  "rate_limited",
  "conflict",
]);

/** A QueryClient tuned for the console: sane retries, no refetch-on-focus storms. */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          const code = (error as { code?: string } | undefined)?.code;
          if (code && NEVER_RETRY.has(code)) return false;
          return failureCount < 2;
        },
        staleTime: 30_000,
        refetchOnWindowFocus: false,
      },
    },
  });
}
